<template>
  <section id="products" class="products-section">
    <div class="section-header">
      <div class="header-line"></div>
      <h2 class="section-title">Our <span>Products</span></h2>
      <div class="header-line"></div>
    </div>
    
    <p class="section-subtitle">Comprehensive Solutions for Educational Excellence</p>
    
    <div class="products-container">
      <div class="product-card" 
        v-for="(product, index) in products" 
        :key="index" 
        :class="{ 'animate': isInView }" 
        :style="{ 'animation-delay': `${0.2 * index}s` }">
        <div class="product-header">
          <div class="product-icon">
            <i :class="product.icon"></i>
          </div>
          <span class="reading-time">
            <i class="fas fa-clock"></i> 2 min read
          </span>
        </div>
        <h3 class="product-title" :class="`title-style-${index + 1}`">{{ product.title }}</h3>
        <div class="product-description-container">
          <p class="product-description" :class="{ 'expanded': expandedDescriptions[index] }">
            {{ product.description }}
          </p>
          <div v-if="!expandedDescriptions[index]" class="fade-overlay"></div>
        </div>
        
        <!-- Read More button -->
        <a href="#" class="action-button read-button" @click.prevent="toggleDescription(index)">
          <i class="fas fa-book-open"></i> {{ expandedDescriptions[index] ? 'Show Less' : 'Read More' }}
        </a>
        
        <div class="secondary-actions">
          <a href="#" class="action-button pdf-button" v-if="false">
            <i class="fas fa-file-pdf"></i> Download PDF
          </a>
          <a href="#" class="action-button video-button" v-if="false">
            <i class="fas fa-play-circle"></i> Watch Video
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ProductsSection',
  data() {
    return {
      isInView: false,
      expandedDescriptions: Array(6).fill(false),
      products: [
        {
          title: 'Faculty Information System (FIS)',
          icon: 'fas fa-user-graduate',
          description: 'A comprehensive platform that centralizes faculty data management, allowing institutions to efficiently track credentials, performance metrics, research output, and teaching activities. This system streamlines administrative processes while providing insights for faculty development and institutional advancement.'
        },
        {
          title: 'Integrated Syllabus System (ISS)',
          icon: 'fas fa-book',
          description: 'Standardize syllabus creation across your institution while allowing appropriate customization. Our system ensures consistency in course information, learning outcomes, assessment methods, and policies while giving instructors flexibility to adapt teaching approaches to their courses.'
        },
        {
          title: 'Integrated Curriculum System (ICS)',
          icon: 'fas fa-graduation-cap',
          description: 'Design, manage, and evaluate curriculum with our integrated system that maps course objectives to program outcomes and institutional goals. This solution enables dynamic curriculum development with built-in assessment capabilities to ensure educational quality and continuous improvement.'
        },
        {
          title: 'Accreditation and Assessment Management System (AAMS)',
          icon: 'fas fa-tasks',
          description: 'Streamline the accreditation process with our comprehensive system for managing evidence collection, self-studies, and continuous improvement initiatives. This platform documents assessment activities, outcomes, and improvement actions to support accreditation requirements and quality assurance efforts.'
        },
        {
          title: 'Timetabling Management System (TTMS)',
          icon: 'fas fa-calendar-alt',
          description: 'Efficiently manage institutional scheduling with our advanced timetabling system. This solution optimizes classroom utilization, prevents scheduling conflicts, and accommodates faculty preferences to create balanced and effective academic schedules.'
        },
        {
          title: 'Strategic Planning and Performance Management System (SPTS)',
          icon: 'fas fa-chart-line',
          description: 'Transform institutional goals into measurable outcomes with our integrated planning and performance management system. This platform facilitates strategic plan development, KPI tracking, and performance analytics to ensure alignment between institutional objectives and operational execution.'
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.checkView);
    // Initial check
    this.checkView();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.checkView);
  },
  methods: {
    toggleDescription(index) {
      this.expandedDescriptions[index] = !this.expandedDescriptions[index];
    },
    checkView() {
      const section = document.querySelector('.products-section');
      if (!section) return;
      
      const position = section.getBoundingClientRect();
      
      // Check if section is in viewport
      if (position.top < window.innerHeight && position.bottom >= 0) {
        this.isInView = true;
      }
    }
  }
}
</script>

<style scoped>
.products-section {
  padding: 8rem 2rem;
  background-color: #fcfaf2;
  position: relative;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.header-line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, #d1c06e, transparent);
  margin: 0 1.5rem;
}

.section-title {
  font-size: 2.5rem;
  color: #333;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
}

.section-title span {
  color: #d1c06e;
  position: relative;
}

.section-title span::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #d1c06e;
}

.section-subtitle {
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.products-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.product-card {
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(209, 192, 110, 0.1);
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  overflow: hidden;
}

.product-card.animate {
  animation: slide-up 0.6s ease forwards;
}

@keyframes slide-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(209, 192, 110, 0.15);
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(to right, #d1c06e, #e0d394);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}

.product-card:hover::before {
  transform: scaleX(1);
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.product-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1c06e, #e0d394);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(209, 192, 110, 0.2);
}

.product-icon i {
  font-size: 1.5rem;
  color: white;
}

.reading-time {
  font-size: 0.85rem;
  color: #888;
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: rgba(209, 192, 110, 0.1);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
}

.reading-time i {
  color: #d1c06e;
}

.product-title {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
  min-height: 3rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-description-container {
  position: relative;
  margin-bottom: 2rem;
}

.product-description {
  color: #666;
  line-height: 1.6;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  transition: all 0.3s ease;
}

.product-description.expanded {
  -webkit-line-clamp: unset;
  display: block;
}

.fade-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 30px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  pointer-events: none;
}

.read-button {
  background-color: rgba(209, 192, 110, 0.1);
  color: #d1c06e;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
}

.secondary-actions {
  display: flex;
  gap: 0.8rem;
  width: 100%;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.pdf-button {
  background-color: white;
  color: #333;
  border: 1px solid #eee;
}

.video-button {
  background-color: rgba(0, 0, 0, 0.05);
  color: #555;
}

.action-button:hover {
  transform: translateX(5px);
}

.pdf-button:hover {
  background-color: #d1c06e;
  color: white;
}

.video-button:hover {
  background-color: #333;
  color: white;
}

.action-button i {
  font-size: 1.1rem;
}

.read-button:hover {
  border-color: #d1c06e;
  color: #d1c06e;
  background-color: rgba(209, 192, 110, 0.2);
}

@media (max-width: 1200px) {
  .products-container {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .products-section {
    padding: 5rem 1.5rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .products-container {
    grid-template-columns: 1fr;
  }
  
  .product-description {
    min-height: auto;
  }
  
  .product-title {
    min-height: auto;
  }
  
  .header-line {
    width: 50px;
  }
}

/* Unique title styles for each product */
.title-style-1 {
  position: relative;
  color: #4a6fc0;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(74, 111, 192, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #4a6fc0;
}

.title-style-1::after {
  content: "FIS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.title-style-2 {
  position: relative;
  color: #2c786c;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(44, 120, 108, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #2c786c;
}

.title-style-2::after {
  content: "ISS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.title-style-3 {
  position: relative;
  color: #6a0572;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(106, 5, 114, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #6a0572;
}

.title-style-3::after {
  content: "ICS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.title-style-4 {
  position: relative;
  color: #e17055;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(225, 112, 85, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #e17055;
}

.title-style-4::after {
  content: "AAMS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.title-style-5 {
  position: relative;
  color: #0a3d62;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(10, 61, 98, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #0a3d62;
}

.title-style-5::after {
  content: "TTMS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.title-style-6 {
  position: relative;
  color: #ff416c;
  font-weight: 800;
  padding: 8px 15px;
  margin-left: -15px;
  background: rgba(255, 65, 108, 0.08);
  border-radius: 0 20px 20px 0;
  box-shadow: inset 5px 0 0 #ff416c;
}

.title-style-6::after {
  content: "SPTS";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  opacity: 0.15;
  font-weight: 900;
  letter-spacing: -1px;
}

.secondary-actions .action-button {
  flex: 1;
  justify-content: center;
}
</style>